// kontoPage/KontoForm.jsx
import { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Autocomplete,
  useMediaQuery,
  Typography,
  useTheme,
  Snackbar,
  Alert,
  Slider,
  Modal,
  IconButton,
  Avatar,
} from "@mui/material";
import { Close, Edit } from "@mui/icons-material";
import { Formik } from "formik";
import * as yup from "yup";
import { useSelector, useDispatch } from "react-redux";
import { setLogin } from "state";
import Cropper from "react-easy-crop";
import getCroppedImg from "../../utils/cropImage";
import { resolveAssetUrl } from "utils/media";
import { SCHOOLS } from "../../constants/schools";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";
const ARTIST_NAME_REGEX = /^(?=.{2,40}$)[\p{L}\p{M}0-9._'\- ]+$/u;

const updateSchema = yup.object().shape({
  firstName: yup.string().required("Erforderlich"),
  lastName: yup.string().required("Erforderlich"),
  username: yup
    .string()
    .matches(/^[a-z0-9_]{3,30}$/, "Benutzername darf nur Kleinbuchstaben (a–z), Zahlen (0–9) und Unterstrich (_) enthalten (3–30 Zeichen)")
    .required("Erforderlich"),
  artistName: yup
    .string()
    .matches(
      ARTIST_NAME_REGEX,
      "Künstlername: 2-30 Zeichen, erlaubt sind Buchstaben, Zahlen, Leerzeichen sowie . _ ' -"
    )
    .required("Erforderlich"),
  school: yup.string().oneOf(["", ...SCHOOLS], "Bitte waehle eine Schule aus der Liste."),
  picture: yup.mixed(),
  currentPassword: yup.string(),
  newPassword: yup
    .string()
    .test(
      "new-password-required-when-current",
      "Bitte gib ein neues Passwort ein.",
      function validateNewPassword(value) {
        if (!this.parent.currentPassword) return true;
        return Boolean(value);
      }
    )
    .test(
      "new-password-min-length",
      "Das neue Passwort muss mindestens 8 Zeichen lang sein.",
      function validateNewPasswordLength(value) {
        if (!value) return true;
        return value.length >= 8;
      }
    ),
  confirmNewPassword: yup
    .string()
    .test(
      "confirm-password-required",
      "Bitte bestätige das neue Passwort.",
      function validateConfirmPassword(value) {
        if (!this.parent.newPassword) return true;
        return Boolean(value);
      }
    )
    .oneOf([yup.ref("newPassword"), null], "Die neuen Passwörter stimmen nicht ueberein."),
});

const KontoForm = () => {
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const { palette } = useTheme();
  const dispatch = useDispatch();
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const user = useSelector((state) => state.user);
  const token = useSelector((state) => state.token);

  // Image cropping states
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [croppedImageUrl, setCroppedImageUrl] = useState("");
  const [showCropper, setShowCropper] = useState(false);
  const [showPasswordFields, setShowPasswordFields] = useState(false);

  useEffect(() => {
    return () => {
      if (croppedImageUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(croppedImageUrl);
      }
    };
  }, [croppedImageUrl]);

  // Initialize form values
  const initialValuesUpdate = {
    firstName: user.firstName || "",
    lastName: user.lastName || "",
    username: user.username || "",
    artistName: user.artistName || "",
    school: user.school || "",
    picture: null,
    currentPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  };

  const handleFormSubmit = async (values, onSubmitProps) => {
    try {
      const formData = new FormData();
      formData.append("firstName", values.firstName);
      formData.append("lastName", values.lastName);
      formData.append("username", values.username);
      formData.append("artistName", values.artistName.trim().replace(/\s+/g, " "));
      formData.append("school", values.school || "");

      if (values.picture) {
        formData.append("picture", values.picture, "profile.jpg");
        formData.append("picturePath", "profile.jpg");
      }

      const response = await fetch(`${BASE_URL}/users/${user._id}/update`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error ${response.status}: ${errorText}`);
      }

      const data = await response.json();

      if (data) {
        dispatch(
          setLogin({
            user: data,
            token: token,
          })
        );

        // Show success message
        setSnackbarMessage("Profil erfolgreich aktualisiert.");
        setSnackbarSeverity("success");
        setOpenSnackbar(true);
      }

      if (values.currentPassword || values.newPassword || values.confirmNewPassword) {
        if (!values.currentPassword || !values.newPassword || !values.confirmNewPassword) {
          throw new Error("Bitte fuelle alle Passwortfelder aus.");
        }

        const changePasswordResponse = await fetch(`${BASE_URL}/users/${user._id}/change-password`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            currentPassword: values.currentPassword,
            newPassword: values.newPassword,
          }),
        });
        const changePasswordData = await changePasswordResponse.json();
        if (!changePasswordResponse.ok) {
          throw new Error(
            changePasswordData.message || "Passwort konnte nicht geaendert werden."
          );
        }

        setSnackbarMessage(changePasswordData.message || "Passwort erfolgreich geaendert.");
        setSnackbarSeverity("success");
        setOpenSnackbar(true);
      }

      // Reset the picture field
      onSubmitProps.setFieldValue("picture", "");
      onSubmitProps.setFieldValue("currentPassword", "");
      onSubmitProps.setFieldValue("newPassword", "");
      onSubmitProps.setFieldValue("confirmNewPassword", "");
      setCroppedImageUrl("");
    } catch (err) {
      console.error("Update failed:", err.message);
      // Show error message
      setSnackbarMessage(err.message || "Fehler beim Aktualisieren des Profils.");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    }
  };

  // Function to read the file
  const readFile = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.addEventListener("load", () => resolve(reader.result), false);
      reader.readAsDataURL(file);
    });
  };

  return (
    <>
      <Formik
        onSubmit={handleFormSubmit}
        initialValues={initialValuesUpdate}
        validationSchema={updateSchema}
        enableReinitialize={true}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
          setFieldTouched,
        }) => {
          // Function to handle crop completion
          const onCropComplete = async () => {
            try {
              const croppedImageBlob = await getCroppedImg(
                imageSrc,
                croppedAreaPixels
              );
              if (croppedImageUrl?.startsWith("blob:")) {
                URL.revokeObjectURL(croppedImageUrl);
              }
              setCroppedImageUrl(URL.createObjectURL(croppedImageBlob));
              setFieldValue("picture", croppedImageBlob);
              setShowCropper(false);
            } catch (e) {
              console.error(e);
            }
          };

          // Function to handle file selection
          const onFileChange = async (acceptedFiles) => {
            if (acceptedFiles && acceptedFiles.length > 0) {
              const file = acceptedFiles[0];
              const imageDataUrl = await readFile(file);
              setImageSrc(imageDataUrl);
              setCrop({ x: 0, y: 0 });
              setZoom(1);
              setShowCropper(true);
            }
          };

          const previewPictureSrc =
            croppedImageUrl ||
            (user.picturePath
              ? resolveAssetUrl(user.picturePath)
              : resolveAssetUrl("/assets/misc/DefaultPFP.png"));

          return (
            <form onSubmit={handleSubmit}>
              <Box
                display="grid"
                gap="30px"
                gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                sx={{
                  "& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
                }}
              >
                <Box
                  gridColumn="span 4"
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                >
                  <Box
                    component="label"
                    aria-label="Profilbild auswählen"
                    sx={{
                      position: "relative",
                      display: "inline-flex",
                      borderRadius: "999px",
                      cursor: "pointer",
                    }}
                  >
                    <Avatar src={previewPictureSrc} sx={{ width: 108, height: 108 }} />
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        borderRadius: "50%",
                        bgcolor: "rgba(0, 0, 0, 0.45)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        opacity: 0,
                        transition: "opacity 0.2s ease",
                        pointerEvents: "none",
                        "& svg": { color: "#fff", fontSize: 28 },
                        "@media (hover: hover)": {
                          "label:hover &, label:focus-within &": {
                            opacity: 1,
                          },
                        },
                      }}
                    >
                      <Edit />
                    </Box>
                    <input
                      type="file"
                      hidden
                      accept=".jpg,.jpeg,.png,.webp"
                      onChange={(e) => onFileChange(e.target.files)}
                    />
                  </Box>
                </Box>
                <TextField
                  label="Vorname"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.firstName}
                  name="firstName"
                  error={
                    Boolean(touched.firstName) && Boolean(errors.firstName)
                  }
                  helperText={touched.firstName && errors.firstName}
                  sx={{ gridColumn: "span 2" }}
                />
                <TextField
                  label="Name"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.lastName}
                  name="lastName"
                  error={Boolean(touched.lastName) && Boolean(errors.lastName)}
                  helperText={touched.lastName && errors.lastName}
                  sx={{ gridColumn: "span 2" }}
                />
                <TextField
                  label="Benutzername"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.username}
                  name="username"
                  error={Boolean(touched.username) && Boolean(errors.username)}
                  helperText={touched.username && errors.username}
                  sx={{ gridColumn: "span 4" }}
                />
                <TextField
                  label="Künstlername"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.artistName}
                  name="artistName"
                  error={Boolean(touched.artistName) && Boolean(errors.artistName)}
                  helperText={touched.artistName && errors.artistName}
                  sx={{ gridColumn: "span 4" }}
                />
                <Autocomplete
                  options={SCHOOLS}
                  value={values.school || null}
                  onChange={(_, value) => {
                    setFieldValue("school", value || "");
                  }}
                  onBlur={() => setFieldTouched("school", true)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Schule"
                      name="school"
                      error={Boolean(touched.school) && Boolean(errors.school)}
                      helperText={touched.school && errors.school}
                    />
                  )}
                  sx={{ gridColumn: "span 4" }}
                />
                <TextField
                  label="E-Mail"
                  value={user.email || ""}
                  disabled
                  sx={{ gridColumn: "span 4" }}
                />

                <Box gridColumn="span 4">
                  <Button
                    type="button"
                    variant={showPasswordFields ? "contained" : "outlined"}
                    onClick={() => setShowPasswordFields((prev) => !prev)}
                    sx={{ mb: showPasswordFields ? 2 : 0 }}
                  >
                    Passwort ändern
                  </Button>
                  {showPasswordFields && (
                    <>
                      <TextField
                        label="Aktuelles Passwort"
                        type="password"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.currentPassword}
                        name="currentPassword"
                        error={Boolean(touched.currentPassword) && Boolean(errors.currentPassword)}
                        helperText={touched.currentPassword && errors.currentPassword}
                        sx={{ gridColumn: "span 4", mb: 2, width: "100%" }}
                      />
                      <TextField
                        label="Neues Passwort"
                        type="password"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.newPassword}
                        name="newPassword"
                        error={Boolean(touched.newPassword) && Boolean(errors.newPassword)}
                        helperText={touched.newPassword && errors.newPassword}
                        sx={{ gridColumn: "span 4", mb: 2, width: "100%" }}
                      />
                      <TextField
                        label="Neues Passwort bestätigen"
                        type="password"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.confirmNewPassword}
                        name="confirmNewPassword"
                        error={
                          Boolean(touched.confirmNewPassword) &&
                          Boolean(errors.confirmNewPassword)
                        }
                        helperText={touched.confirmNewPassword && errors.confirmNewPassword}
                        sx={{ gridColumn: "span 4", width: "100%" }}
                      />
                    </>
                  )}
                </Box>

                <Modal
                  open={showCropper}
                  onClose={() => setShowCropper(false)}
                  disableScrollLock
                >
                  <Box
                    sx={{
                      position: "fixed",
                      inset: 0,
                      bgcolor: "rgba(0, 0, 0, 0.84)",
                      display: "flex",
                      alignItems: { xs: "stretch", sm: "center" },
                      justifyContent: "center",
                      p: { xs: 0, sm: 2 },
                      zIndex: 1400,
                    }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        maxWidth: 720,
                        height: { xs: "100vh", sm: "82vh" },
                        bgcolor: "background.paper",
                        borderRadius: { xs: 0, sm: 2 },
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Box
                        sx={{
                          px: 1.5,
                          py: 1,
                          borderBottom: "1px solid",
                          borderColor: "divider",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography fontWeight={600}>Profilbild zuschneiden</Typography>
                        <IconButton
                          onClick={() => setShowCropper(false)}
                          size="small"
                          aria-label="Schließen"
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </Box>
                      <Box
                        sx={{
                          position: "relative",
                          flex: 1,
                          bgcolor: "rgba(0, 0, 0, 0.85)",
                        }}
                      >
                        {imageSrc && (
                          <Cropper
                            image={imageSrc}
                            crop={crop}
                            zoom={zoom}
                            aspect={1}
                            cropShape="round"
                            showGrid={false}
                            onCropChange={setCrop}
                            onZoomChange={setZoom}
                            onCropComplete={(_, areaPixels) => {
                              setCroppedAreaPixels(areaPixels);
                            }}
                            maxZoom={3}
                          />
                        )}
                      </Box>
                      <Box
                        sx={{
                          p: 2,
                          pb: "max(16px, env(safe-area-inset-bottom))",
                          borderTop: "1px solid",
                          borderColor: "divider",
                          display: "flex",
                          flexDirection: "column",
                          gap: 1.5,
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Zoom
                        </Typography>
                        <Slider
                          value={zoom}
                          min={1}
                          max={3}
                          step={0.1}
                          onChange={(_, nextZoom) => setZoom(nextZoom)}
                          aria-label="Zoom"
                        />
                        <Box display="flex" justifyContent="space-between" gap={1}>
                          <Button
                            variant="outlined"
                            onClick={() => setShowCropper(false)}
                            sx={{ flex: 1 }}
                          >
                            Abbrechen
                          </Button>
                          <Button variant="contained" onClick={onCropComplete} sx={{ flex: 1 }}>
                            Zuschneiden
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Modal>
              </Box>

              {/* Submit Button */}
              <Box>
                <Button
                  fullWidth
                  type="submit"
                  sx={{
                    m: "2rem 0",
                    p: "1rem",
                    backgroundColor: palette.primary.main,
                    color: palette.background.alt,
                    "&:hover": { color: palette.primary.main },
                  }}
                >
                  Aktualisieren
                </Button>
              </Box>
            </form>
          );
        }}
      </Formik>

      {/* Snackbar for feedback messages */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default KontoForm;
